import React, { useState, useEffect, useContext } from 'react';
import AuthContext from './AuthContext';

const UsageDashboard = () => {
  const { user } = useContext(AuthContext);
  const [subscriptionData, setSubscriptionData] = useState(null);
  const [loading, setLoading] = useState(true);

  const getBackendUrl = () => {
    return process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';
  };

  useEffect(() => {
    const fetchSubscriptionData = async () => {
      if (!user) return;

      try {
        const response = await fetch(`${getBackendUrl()}/api/subscription/current`, {
          credentials: 'include',
        });
        
        if (response.ok) {
          const data = await response.json();
          setSubscriptionData(data);
        }
      } catch (error) {
        console.error('Error fetching subscription data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSubscriptionData();
  }, [user]);

  const getUsagePercentage = (used, limit) => {
    if (limit === 999999) return 0; // Unlimited
    return Math.min((used / limit) * 100, 100);
  };

  const getUsageColor = (percentage) => {
    if (percentage >= 90) return 'text-red-400';
    if (percentage >= 70) return 'text-yellow-400';
    return 'text-green-400';
  };

  const getProgressBarColor = (percentage) => {
    if (percentage >= 90) return 'bg-red-400';
    if (percentage >= 70) return 'bg-yellow-400';
    return 'bg-green-400';
  };

  if (loading) {
    return (
      <div className="bg-gray-800 rounded-lg p-6">
        <div className="animate-pulse">
          <div className="h-6 bg-gray-700 rounded w-1/2 mb-4"></div>
          <div className="space-y-3">
            <div className="h-4 bg-gray-700 rounded"></div>
            <div className="h-4 bg-gray-700 rounded w-3/4"></div>
            <div className="h-4 bg-gray-700 rounded w-1/2"></div>
          </div>
        </div>
      </div>
    );
  }

  if (!subscriptionData) {
    return (
      <div className="bg-gray-800 rounded-lg p-6">
        <p className="text-gray-400">Unable to load subscription data.</p>
      </div>
    );
  }

  const { subscription, usage, package_config } = subscriptionData;

  return (
    <div className="bg-gray-800 rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h3 className="text-xl font-bold text-white">Usage Dashboard</h3>
        <span className={`px-3 py-1 rounded-full text-sm font-medium ${
          subscription.subscription_name === 'Explorer' ? 'bg-blue-900 text-blue-200' :
          subscription.subscription_name === 'Navigator' ? 'bg-green-900 text-green-200' :
          subscription.subscription_name === 'Advocate' ? 'bg-purple-900 text-purple-200' :
          'bg-yellow-900 text-yellow-200'
        }`}>
          {subscription.subscription_name}
        </span>
      </div>

      <div className="space-y-4">
        {/* AI Interactions */}
        <div className="bg-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-white font-medium">AI Interactions</span>
            <span className={`text-sm font-medium ${getUsageColor(getUsagePercentage(usage?.usage?.ai_interactions?.used || 0, package_config?.features?.ai_interactions || 0))}`}>
              {usage?.usage?.ai_interactions?.used || 0} / {package_config?.features?.unlimited_interactions ? 'Unlimited' : (package_config?.features?.ai_interactions || 0)}
            </span>
          </div>
          {!package_config?.features?.unlimited_interactions && (
            <div className="w-full bg-gray-600 rounded-full h-2">
              <div 
                className={`h-2 rounded-full ${getProgressBarColor(getUsagePercentage(usage?.usage?.ai_interactions?.used || 0, package_config?.features?.ai_interactions || 0))}`}
                style={{ width: `${getUsagePercentage(usage?.usage?.ai_interactions?.used || 0, package_config?.features?.ai_interactions || 0)}%` }}
              ></div>
            </div>
          )}
        </div>

        {/* Document Uploads */}
        <div className="bg-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-white font-medium">Document Uploads</span>
            <span className={`text-sm font-medium ${getUsageColor(getUsagePercentage(usage?.usage?.document_uploads?.used || 0, package_config?.features?.document_uploads || 0))}`}>
              {usage?.usage?.document_uploads?.used || 0} / {package_config?.features?.unlimited_uploads ? 'Unlimited' : (package_config?.features?.document_uploads || 0)}
            </span>
          </div>
          {!package_config?.features?.unlimited_uploads && (
            <div className="w-full bg-gray-600 rounded-full h-2">
              <div 
                className={`h-2 rounded-full ${getProgressBarColor(getUsagePercentage(usage?.usage?.document_uploads?.used || 0, package_config?.features?.document_uploads || 0))}`}
                style={{ width: `${getUsagePercentage(usage?.usage?.document_uploads?.used || 0, package_config?.features?.document_uploads || 0)}%` }}
              ></div>
            </div>
          )}
        </div>

        {/* Document Scans */}
        <div className="bg-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-white font-medium">Document Scans</span>
            <span className={`text-sm font-medium ${getUsageColor(getUsagePercentage(usage?.usage?.document_scans?.used || 0, package_config?.features?.document_scans || 0))}`}>
              {usage?.usage?.document_scans?.used || 0} / {package_config?.features?.unlimited_scans ? 'Unlimited' : (package_config?.features?.document_scans || 0)}
            </span>
          </div>
          {!package_config?.features?.unlimited_scans && (
            <div className="w-full bg-gray-600 rounded-full h-2">
              <div 
                className={`h-2 rounded-full ${getProgressBarColor(getUsagePercentage(usage?.usage?.document_scans?.used || 0, package_config?.features?.document_scans || 0))}`}
                style={{ width: `${getUsagePercentage(usage?.usage?.document_scans?.used || 0, package_config?.features?.document_scans || 0)}%` }}
              ></div>
            </div>
          )}
        </div>

        {/* AI Agents */}
        <div className="bg-gray-700 rounded-lg p-4">
          <div className="flex items-center justify-between mb-2">
            <span className="text-white font-medium">AI Agents</span>
            <span className={`text-sm font-medium ${getUsageColor(getUsagePercentage(usage?.ai_agents_used?.length || 0, package_config?.features?.ai_agents || 0))}`}>
              {usage?.ai_agents_used?.length || 0} / {package_config?.features?.unlimited_agents ? 'All 8' : (package_config?.features?.ai_agents || 0)}
            </span>
          </div>
          {!package_config?.features?.unlimited_agents && (
            <div className="w-full bg-gray-600 rounded-full h-2">
              <div 
                className={`h-2 rounded-full ${getProgressBarColor(getUsagePercentage(usage?.ai_agents_used?.length || 0, package_config?.features?.ai_agents || 0))}`}
                style={{ width: `${getUsagePercentage(usage?.ai_agents_used?.length || 0, package_config?.features?.ai_agents || 0)}%` }}
              ></div>
            </div>
          )}
        </div>
      </div>

      {/* Subscription Details */}
      <div className="mt-6 pt-4 border-t border-gray-600">
        <div className="flex items-center justify-between text-sm text-gray-400">
          <span>Status: {subscription.status}</span>
          {subscription.current_period_end && (
            <span>Next billing: {new Date(subscription.current_period_end).toLocaleDateString()}</span>
          )}
        </div>
      </div>

      {/* Upgrade Prompt for Free Users */}
      {subscription.subscription_name === 'Explorer' && (
        <div className="mt-4 p-4 bg-gradient-to-r from-green-900/20 to-blue-900/20 rounded-lg border border-green-400/30">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-white font-medium">Upgrade for More Features</p>
              <p className="text-gray-400 text-sm">Get more AI interactions, document uploads, and access to additional agents.</p>
            </div>
            <button className="bg-green-400 hover:bg-green-500 text-black font-bold py-2 px-4 rounded-lg transition duration-300">
              Upgrade
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default UsageDashboard;